<template>
  <div class="inpatient-treatment-page">
    <!-- Header -->
    <div class="header" v-if="can('view-header (inpatient timeline)')">
      <h2>🏥 Inpatient Timeline</h2>
    </div>

    <!-- Biodata -->
    <div class="biodata-grid" v-if="can('view-biodata (inpatient timeline)')">
      <div class="biodata-header">
        <div><strong>Patient Name:</strong> {{ patient.name }}</div>
        <div><strong>Patient No:</strong> {{ patient.hospital_no }}</div>
      </div>

      <div class="biodata-body">
        <div>
          <strong>Admission Date:</strong>
          {{ formatDateTime(patient.admission_date) }}
        </div>
        <div><strong>Age:</strong> {{ patient.age }}</div>
        <div><strong>Gender:</strong> {{ patient.gender }}</div>
        <div><strong>Ward:</strong> {{ patient.ward }}</div>
        <div><strong>Bed:</strong> {{ patient.bed }}</div>
        <div><strong>Consultant:</strong> {{ patient.consultant }}</div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-bar" v-if="can('view-action-bar (inpatient timeline)')">
      <button
        v-if="can('add-diagnosis (inpatient timeline)')"
        @click="openDiagnosisModal"
        class="btn m-0 btn-diagnosis"
      >
        + Diagnosis
      </button>

      <button
        v-if="can('add-prescription (inpatient timeline)')"
        @click="openPrescriptionModal"
        class="btn m-0 btn-prescription"
      >
        + Prescription
      </button>

      <button
        v-if="can('issue-prescription (inpatient timeline)')"
        @click="issue_display"
        class="btn m-0 btn-prescription-issue"
      >
        + Issue Prescription
      </button>

      <button
        v-if="can('add-vitals (inpatient timeline)')"
        @click="openVitalsModal"
        class="btn m-0 btn-vitals"
      >
        + Vitals
      </button>

      <button
        v-if="can('add-transfusion (inpatient timeline)')"
        @click="show_transfusion_modal"
        class="btn m-0 btn-transfusion"
      >
        + Transfusion
      </button>

      <button
        v-if="can('add-nursing-note (inpatient timeline)')"
        @click="show_nursenotes_modal"
        class="btn m-0 btn-nursing"
      >
        + Nursing Note
      </button>

      <button
        v-if="can('add-lab-request (inpatient timeline)')"
        @click="openPatientServiceModal"
        class="btn m-0 btn-lab"
      >
        + Lab Request
      </button>

      <button
        v-if="can('add-radiology-request (inpatient timeline)')"
        @click="openPatientServiceModal"
        class="btn m-0 btn-radiology"
      >
        + Radiology
      </button>

      <button
        v-if="can('add-doctor-note (inpatient timeline)')"
        @click="openConsultationNotes"
        class="btn m-0 btn-progress"
      >
        + Doctor's Notes
      </button>

      <button
        v-if="can('add-procedure (inpatient timeline)')"
        @click="openPatientServiceModal"
        class="btn m-0 btn-procedure"
      >
        + Procedure
      </button>

      <button
        v-if="can('add-turning-sheet (inpatient timeline)')"
        @click="turning_show"
        class="btn m-0 btn-turning-sheet"
      >
        + Turning Sheet
      </button>

      <button
        v-if="can('add-discharge-plan (inpatient timeline)')"
        @click="discharge_show"
        class="btn m-0 btn-discharge"
      >
        + Discharge Plan
      </button>

      <!-- optional change bed button (kept hidden unless permission) -->
      <button
        v-if="can('change-ward-bed (inpatient timeline)')"
        @click="show_change_bed"
        class="btn m-0 btn-ward"
      >
        ↔ Change Ward/Bed
      </button>
    </div>

    <!-- Diagnosis & Vitals Section -->
    <div
      class="section"
      v-if="can('view-diagnosis-vitals (inpatient timeline)')"
    >
      <h3>📊 Diagnosis & Vitals</h3>
      <div class="section-grid">
        <!-- Diagnoses -->
        <div class="card" v-if="can('view-diagnosis (inpatient timeline)')">
          <h4>Diagnoses</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Diagnosis</th>
                <th>Type</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="diag in diagnosis" :key="diag.id">
                <td>{{ formatDate(diag.created_at) }}</td>
                <td>{{ diag.diagnosis }}</td>
                <td>{{ diag.diagnosis_type }}</td>
                <td>{{ diag.diagnosis_stage }}</td>
              </tr>
              <tr v-if="diagnosis.length === 0">
                <td colspan="4">No diagnoses recorded.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Vitals -->
        <div class="card" v-if="can('view-vitals (inpatient timeline)')">
          <h4>Vitals</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Time</th>
                <th>BP</th>
                <th>Pulse</th>
                <th>Temp</th>
                <th>Resp</th>
                <th>O₂ Sat</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="vital in triage" :key="vital.id">
                <td>{{ formatDate(vital.created_at) }}</td>
                <td>{{ vital.blood_pressure }}</td>
                <td>{{ vital.heart_rate }}</td>
                <td>{{ vital.temperature }}</td>
                <td>{{ vital.respiratory_rate }}</td>
                <td>{{ vital.oxygen_saturation }}</td>
              </tr>
              <tr v-if="triage.length === 0">
                <td colspan="6">No vitals recorded.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Drugs Section -->
    <div class="section" v-if="can('view-drugs (inpatient timeline)')">
      <h3>💊 Drugs</h3>
      <div class="section-grid">
        <!-- Prescriptions -->
        <div class="card" v-if="can('view-prescriptions (inpatient timeline)')">
          <h4>Prescriptions</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Drug</th>
                <th>Dosage</th>
                <th>Frequency</th>
                <th>Instruction</th>
                <th>Prescribed By</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="drug in prescriptions" :key="drug.pres_id">
                <td>
                  <ItemName
                    :ItemName="drug.item_name"
                    :ItemBrand="drug.brand_name"
                    :ItemStrength="drug.item_strength"
                  />
                </td>
                <td>{{ drug.dosage }}</td>
                <td>{{ drug.frequency }}</td>
                <td>{{ drug.dosage + " X " + drug.frequency }}</td>
                <td>{{ drug.prescribed_by || drug.doctor_name || "—" }}</td>
              </tr>
              <tr v-if="prescriptions.length === 0">
                <td colspan="5">No prescriptions.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Administered Drugs -->
        <div
          class="card"
          v-if="can('view-administered-drugs (inpatient timeline)')"
        >
          <h4>Administered Drugs</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Time</th>
                <th>Drug</th>
                <th>Dosage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="drug in dispensed" :key="drug.pres_id">
                <td>{{ formatDate(drug.created_at) }}</td>
                <td>
                  <ItemName
                    :ItemName="drug.item_name"
                    :ItemBrand="drug.brand_name"
                    :ItemStrength="drug.item_strength"
                  />
                </td>
                <td>{{ drug.dosage }}</td>
              </tr>
              <tr v-if="dispensed.length === 0">
                <td colspan="3">No administered drug records.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Transfusions -->
        <!-- <div class="card" v-if="can('view-transfusions (inpatient timeline)')">
          <h4>Transfusions</h4>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Blood Group</th>
                <th>Volume</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="trans in blood_transfusion" :key="trans.id">
                <td>
                  {{ trans.transfusion_date || formatDate(trans.created_at) }}
                </td>
                <td>{{ trans.blood_group }}</td>
                <td>{{ trans.volume_ml ? trans.volume_ml + " ml" : "—" }}</td>
                <td>{{ trans.status }}</td>
                <td>{{ trans.notes }}</td>
              </tr>
              <tr v-if="blood_transfusion.length === 0">
                <td colspan="5">No transfusion records.</td>
              </tr>
            </tbody>
          </table>
        </div> -->
      </div>
    </div>

    <!-- Nursing Section -->
    <div class="section" v-if="can('view-nursing (inpatient timeline)')">
      <h3>🧑‍⚕️ Nursing</h3>
      <div class="section-grid">
        <div class="card" v-if="can('view-nursing-notes (inpatient timeline)')">
          <h4>Nursing Notes</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th width="20%">Time & Nurse</th>
                <th width="80%">Note</th>
                <!-- <th>Nurse</th> -->
              </tr>
            </thead>
            <tbody>
              <tr v-for="note in nurse_notes" :key="note.id">
                <td style="padding: 10px; vertical-align: top">
                  <div style="display: flex; flex-direction: column; gap: 4px">
                    <span style="font-size: 13px; color: #6c757d">
                      {{ formatDate(note.created_at) }}
                    </span>

                    <span
                      style="font-size: 14px; font-weight: 600; color: #343a40"
                    >
                      Nurse:{{ note.nurse_name }}
                    </span>
                  </div>
                </td>
                <td>{{ note.note_text }}</td>
                <!-- <td>{{ note.nurse_name }}</td> -->
              </tr>
              <tr v-if="nurse_notes.length === 0">
                <td colspan="3">No nursing notes.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card" v-if="can('view-turning-sheet (inpatient timeline)')">
          <h4>Turning Sheet</h4>

          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Time</th>
                <th>Position</th>
                <th>Staff</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in turnHistory" :key="index">
                <td>{{ entry.time }}</td>
                <td
                  :class="{ 'highlight-position': entry.position === 'Prone' }"
                >
                  {{ entry.position }}
                </td>
                <td>{{ entry.signature }}</td>
                <td>{{ entry.notes || "N/A" }}</td>
              </tr>
              <tr v-if="turnHistory.length === 0">
                <td colspan="4" class="empty-message">
                  No turns logged in the last 24 hours.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Labs & Radiology -->
    <div class="section" v-if="can('view-labs-radiology (inpatient timeline)')">
      <h3>🧪 Labs & Radiology</h3>
      <div class="section-grid">
        <div class="card" v-if="can('view-lab-tests (inpatient timeline)')">
          <h4>Lab Tests</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Test</th>
                <th>Status</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="laboratory in lab" :key="laboratory.id">
                <td>{{ formatDate(laboratory.created_at) }}</td>
                <td>{{ laboratory.name }}</td>
                <td>
                  {{
                    laboratory.attended == 0
                      ? "pending"
                      : laboratory.attended == 1
                      ? "Ongoing"
                      : "completed"
                  }}
                </td>
                <td>
                  <a href="#" @click.prevent="viewLabResult(laboratory)"
                    >View</a
                  >
                </td>
              </tr>
              <tr v-if="lab.length === 0">
                <td colspan="4">No lab tests.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="card"
          v-if="can('view-radiology-tests (inpatient timeline)')"
        >
          <h4>Radiology</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Imaging</th>
                <th>Status</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rad in radiology" :key="rad.id">
                <td>{{ formatDate(rad.created_at) }}</td>
                <td>{{ rad.name }}</td>
                <td>{{ rad.attended == 0 ? "pending" : "completed" }}</td>
                <td>
                  <a href="#" @click.prevent="viewRadiologyReport(rad)">View</a>
                </td>
              </tr>
              <tr v-if="radiology.length === 0">
                <td colspan="4">No radiology requests.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Consultants -->
    <div class="section" v-if="can('view-consultants (inpatient timeline)')">
      <h3>👨‍⚕️ Consultants</h3>
      <div class="section-grid">
        <div class="card" v-if="can('view-doctor-notes (inpatient timeline)')">
          <h4>Doctor's Notes</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th width="20%">Date</th>
                <th width="80%">Note</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="note in doctor_notes" :key="note.id">
                <td>{{ formatDate(note.created_at) }}</td>
                <td>{{ note.note }}</td>
              </tr>
              <tr v-if="doctor_notes.length === 0">
                <td colspan="2">No doctor's notes.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- <div
          class="card"
          v-if="can('view-doctor-reviews (inpatient timeline)')"
        >
          <h4>Doctor Reviews</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Doctor</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rev in doctor_reviews" :key="rev.id">
                <td>{{ formatDate(rev.created_at) }}</td>
                <td>{{ rev.doctor_name }}</td>
                <td>{{ rev.review_text }}</td>
              </tr>
              <tr v-if="doctor_reviews.length === 0">
                <td colspan="3">No doctor reviews.</td>
              </tr>
            </tbody>
          </table>
        </div> -->

        <div class="card" v-if="can('view-procedures (inpatient timeline)')">
          <h4>Procedures</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Procedure</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="proc in procedure" :key="proc.id">
                <td>{{ formatDate(proc.created_at) }}</td>
                <td>{{ proc.name }}</td>
                <td>{{ proc.attended == 0 ? "pending" : "completed" }}</td>
              </tr>
              <tr v-if="procedure.length === 0">
                <td colspan="3">No procedures.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Transfussion & Discharge plan-->
    <div
      class="section"
      v-if="
        can('view-transfusions (inpatient timeline)') ||
        can('view-discharge-plan (inpatient timeline)')
      "
    >
      <h3>🧪 Blood Tranfussion & Discharge Plan</h3>
      <div class="section-grid">
        <!-- Transfusions -->
        <div class="card" v-if="can('view-transfusions (inpatient timeline)')">
          <h4>Transfusions</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Time</th>
                <th>Blood Group</th>
                <th>Volume</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="trans in blood_transfusion" :key="trans.id">
                <td>
                  {{ trans.transfusion_date || formatDate(trans.created_at) }}
                </td>
                <td>{{ trans.blood_group }}</td>
                <td>{{ trans.volume_ml ? trans.volume_ml + " ml" : "—" }}</td>
                <td>{{ trans.status }}</td>
                <td>{{ trans.notes }}</td>
              </tr>
              <tr v-if="blood_transfusion.length === 0">
                <td colspan="5">No transfusion records.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="card"
          v-if="can('view-discharge-plan (inpatient timeline)')"
        >
          <h4>Discharge Plan</h4>
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th width="30%">DischargeDate & Doctor</th>
                <!-- <th width="10%">Doctor</th> -->
                <th width="20%">Condition</th>
                <!-- <th>Next Visit</th> -->
                <th width="50%">Discharge Summary</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in discharges"
                :key="d.id"
                style="border-bottom: 1px solid #dee2e6"
              >
                <!-- Date & Doctor -->
                <td style="padding: 10px; vertical-align: top">
                  <div style="display: flex; flex-direction: column; gap: 4px">
                    <div style="font-size: 12px; color: #6c757d">
                      Date: {{ d.discharge_date }}
                    </div>

                    <div
                      style="font-size: 14px; font-weight: 600; color: #343a40"
                    >
                      Dr. {{ d.doctor_name }}
                    </div>
                  </div>
                </td>

                <!-- Condition -->
                <td style="padding: 10px; vertical-align: top">
                  <div
                    style="font-size: 13px; color: #495057; line-height: 1.4"
                  >
                    {{ d.condition }}
                  </div>
                </td>

                <!-- Notes -->
                <td style="padding: 10px; vertical-align: top">
                  <div
                    style="
                      font-size: 13px;
                      color: #495057;
                      line-height: 1.5;
                      word-break: break-word;
                    "
                  >
                    {{ d.notes }}
                  </div>
                </td>
              </tr>
              <tr v-if="discharges.length === 0">
                <td colspan="2">No discharge plan.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modals (same as original; opening already permission-checked by buttons) -->
    <AddDiagnosisModal
      v-if="showDiagnosisModal"
      :visit_id="visit_id"
      @close-add-diagnosis-modal="closeDiagnosisModal"
    />

    <add_prescription_modal
      v-if="showPrescriptionModal"
      :visit_id="visit_id"
      :store_id="2"
      pres_type="in_prescription"
      @close-add-pres-modal="closePrescriptionModal"
    />

    <take_vitals_modal
      :visit_id="visit_id"
      v-if="showVitalsModal"
      @close="closeVitalsModal"
    />

    <add_patient_service_modal
      v-if="showPatientServiceModal"
      :visitId="visit_id"
      @close-add-service-modal="closePatientServiceModal"
    />

    <consultation_notes
      v-if="showConsultationNotes"
      @close-add-doc-note-modal="closeConsultationNotes"
      :visit_id="visit_id"
    />

    <Transfusion
      v-if="showTransfusionModal"
      :transfusion="selectedTransfusion"
      :visit_id="visit_id"
      @close-transfusion-mod="close_transfusion_modal"
    />

    <NurseNotes
      v-if="showNurseNotesModal"
      :note="selectedNote"
      :visit_id="visit_id"
      @close-nurse-notes-modal="hide_nursenotes_modal"
    />

    <DischargeModal
      v-if="showDischargeModal"
      :patient="selectedPatient"
      :visit_id="visit_id"
      @close-discharge-modal="discharge_close"
    />

    <changeBed
      @close-change-bed-modal="hide_change_bed"
      v-if="showChangeBed"
      :patient="1"
    />
    <issue_drugs
      v-if="showIssueDrugsModal"
      :patient="patient"
      :visit_id="visit_id"
      @close-issue-modal="issue_close"
    />
    <tunning_sheet_modal
      v-if="showTurningsModal"
      :patient="patient"
      :visit_id="visit_id"
      @close-turning-modal="turning_close"
    />
  </div>
</template>

<script>
import AddDiagnosisModal from "../visits_management/diagnosis/AddDiagnosisModal.vue";
import add_prescription_modal from "../visits_management/pharmacy/add_prescription_modal.vue";
import take_vitals_modal from "../Patients/components/take_vitals_modal.vue";
import add_patient_service_modal from "../visits_management/add_patient_service_modal.vue";
import consultation_notes from "../visits_management/consultation/add_doctors_note_modal.vue";
import Transfusion from "./blood_transfusion.vue";
import NurseNotes from "./nurse_notes_modal.vue";
import DischargeModal from "./discharge_modal.vue";
import changeBed from "./change_bed_modal.vue";
// import consultation_notes from "../visits_management/consultation/add_doctors_note_modal.vue";
import ItemName from "../Inventory/components/ItemName.vue";
// import lab_results_modal2 from "./lab_results_modal2.vue";
import formatDateTime from "../../timezone/utc_local_time";
import issue_drugs from "./issue_drugs.vue";

import can from "../../permissions/permissions";
import { generalApiRequest } from "../../api/apiIndex";
import tunning_sheet_modal from "./tunning_sheet_modal.vue";

export default {
  name: "InpatientTreatmentFull",
  data() {
    return {
      patient: {
        name: "",
        hospital_no: "",
        admission_date: "",
        complaints: "",
        age: "",
        gender: "",
        dob: "",
        ward: "",
        bed: "",
        primary_diagnosis: "",
        consultant: "",
      },
      visit_id: this.$route.params.visitID,

      blood_transfusion: [],
      discharges: [],
      doctor_notes: [],
      lab: [],
      nurse_notes: [],
      prescriptions: [],
      dispensed: [],
      procedure: [],
      radiology: [],
      diagnosis: [],
      triage: [],
      administered_drugs: [],
      turning_sheet: [],
      doctor_reviews: [],
      turnHistory: [],

      //   display modal toggles
      showDiagnosisModal: false,
      showPrescriptionModal: false,
      showVitalsModal: false,
      showPatientServiceModal: false,
      showConsultationNotes: false,
      showTransfusionModal: false,
      showNurseNotesModal: false,
      showDischargeModal: false,
      showChangeBed: false,
      showLabResultsModal: true,
      showIssueDrugsModal: false,
      showTurningsModal: false,
    };
  },
  components: {
    AddDiagnosisModal,
    add_prescription_modal,
    take_vitals_modal,
    add_patient_service_modal,
    consultation_notes,
    Transfusion,
    NurseNotes,
    DischargeModal,
    changeBed,
    ItemName,
    issue_drugs,
    tunning_sheet_modal,
    // lab_results_modal2,
  },

  methods: {
    can,
    formatDateTime,
    turning_show() {
      this.showTurningsModal = true;
      this.get_inpatient_data();
    },
    turning_close() {
      this.showTurningsModal = false;
      this.get_inpatient_data();
    },
    issue_display() {
      this.showIssueDrugsModal = true;
    },
    issue_close() {
      this.showIssueDrugsModal = false;
      this.get_inpatient_data();
    },
    show_change_bed() {
      this.showChangeBed = true;
    },
    hide_change_bed() {
      this.showChangeBed = false;
      this.get_inpatient_data();
    },
    discharge_show() {
      this.showDischargeModal = true;
    },
    discharge_close() {
      this.showDischargeModal = false;
      this.get_inpatient_data();
    },
    show_nursenotes_modal() {
      this.showNurseNotesModal = true;
    },
    hide_nursenotes_modal() {
      this.showNurseNotesModal = false;
      this.get_inpatient_data();
    },
    show_transfusion_modal() {
      this.showTransfusionModal = true;
    },
    close_transfusion_modal() {
      this.get_inpatient_data();
      this.showTransfusionModal = false;
    },
    // Diagnosis
    openDiagnosisModal() {
      this.showDiagnosisModal = true;
    },
    closeDiagnosisModal() {
      this.showDiagnosisModal = false;
      this.get_inpatient_data();
    },

    // Prescription
    openPrescriptionModal() {
      this.showPrescriptionModal = true;
    },
    closePrescriptionModal() {
      this.showPrescriptionModal = false;
      this.get_inpatient_data();
    },

    // Vitals
    openVitalsModal() {
      this.showVitalsModal = true;
    },
    closeVitalsModal() {
      this.showVitalsModal = false;
      this.get_inpatient_data();
    },

    // Patient Service
    openPatientServiceModal() {
      this.showPatientServiceModal = true;
    },
    closePatientServiceModal() {
      this.showPatientServiceModal = false;
      this.get_inpatient_data();
    },

    // Consultation Notes
    openConsultationNotes() {
      // console.log("this is the visit_id " + this.visit_id);

      this.showConsultationNotes = true;
    },
    closeConsultationNotes() {
      this.showConsultationNotes = false;
      this.get_inpatient_data();
    },
    formatDate(param) {
      return formatDateTime(param);
    },
    async get_inpatient_data() {
      const response = await generalApiRequest(
        "inpatient_data/" + this.visit_id,
        "GET",
        {},
        1,
      );

      if (response.visis_status == "ended") {
        window.location.href = "/visit_summary/" + this.visit_id;
      }
      console.log("=====================================");

      console.log(response);

      const newPatient = {
        name: response.data.patient.patient_name,
        hospital_no:
          "IPD " +
          response.data.patient.ipd +
          " || OPD " +
          response.data.patient.opd,
        age: response.data.patient.age,
        dob: response.data.patient.dob,
        complaints: response.data.patient.complaints,
        gender: response.data.patient.gender == 1 ? "Male" : "Female",
        ward: response.data.patient.ward_name,
        bed: response.data.patient.bed_number,
        admission_date: response.data.patient.created_at,
        primary_diagnosis: "",
        consultant: "",
      };
      // console.log(newPatient);

      this.patient = newPatient;

      // visit_data

      this.blood_transfusion = response.data.blood_transfusions;
      this.discharges = response.data.discharges;
      // this.discharges
      this.doctor_notes = response.data.doctor_notes;
      this.lab = response.data.lab;
      this.turnHistory = response.data.turningSheets;
      // console.log(response.data.lab);

      this.nurse_notes = response.data.nurse_notes;
      this.prescriptions = response.data.prescriptions;
      this.procedure = response.data.procedure;
      this.radiology = response.data.radiology;
      this.diagnosis = response.data.diagnosis;
      this.triage = response.data.triage;
      this.dispensed = response.data.dispensed;
    },
  },
  mounted() {
    console.log(
      "===============" +
        this.can("view-header (inpatient timeline)") +
        "===================",
    );
    this.get_inpatient_data();
  },
};
</script>

<style scoped>
.inpatient-treatment-page {
  padding: 1.5rem;
  font-family: Arial, sans-serif;
  color: #000;
}

/* Header */
.header h2 {
  font-size: 1.7rem;
  margin-bottom: 0.3rem;
}

.biodata-grid {
  width: 100%;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 14px;
  color: #000;
  font-family: Arial, sans-serif;
}

/* --- Header Line (Name + Hospital No) --- */
.biodata-header {
  background-color: beige;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #222;
}

.biodata-header div {
  white-space: normal;
  word-wrap: break-word;
}

/* --- Body Lines --- */
.biodata-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 6px 16px;
  font-size: 0.9em;
}

.biodata-body div {
  white-space: normal;
  word-wrap: break-word;
}

.biodata-body strong {
  color: #333;
  margin-right: 4px;
}

/* .biodata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.5rem;
  background: #f9f9f9;
  padding: 0.8rem;
  border: 1px solid #ddd;
  margin-bottom: 1rem;
  border-radius: 0.3rem;
} */

/* Action bar */
.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.action-bar button {
  padding: 0.5rem 1rem;
  color: #fff;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Button colors */
.btn-diagnosis {
  background: #1e88e5;
}
.btn-prescription {
  background: #43a047;
}
.btn-prescription-issue {
  background: #6c910f;
}
.btn-turning-sheet {
  background: #4d6905;
}
.btn-doc-review {
  background: #6c910f;
}
.btn-vitals {
  background: #fb8c00;
}
.btn-fluids {
  background: #8e24aa;
}
.btn-transfusion {
  background: #e53935;
}
.btn-nursing {
  background: #6d4c41;
}
.btn-lab {
  background: #fbc02d;
  color: #000;
}
.btn-radiology {
  background: #00acc1;
}
.btn-progress {
  background: #00897b;
}
.btn-review {
  background: #757575;
}
.btn-procedure {
  background: #ff7043;
}
.btn-discharge {
  background: #2e7d32;
}
.btn-ward {
  background: #3949ab;
}

/* Section */
.section {
  margin-bottom: 2rem;
}

.section h3 {
  font-size: 1.3rem;
  margin-bottom: 0.8rem;
  border-bottom: 2px solid #2786f7;
  padding-bottom: 0.3rem;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  gap: 1rem;
}

.card {
  background: #fff;
  border: 1px solid #ddd;
  padding: 0.8rem;
  border-radius: 0.4rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.card h4 {
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
}

/* Tables */
/* table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

thead {
  background: #f2f2f2;
}

th,
td {
  border: 1px solid #ddd;
  padding: 0.4rem;
  text-align: left;
} */

.table-container {
  width: 100%;
  overflow-x: auto; /* allows horizontal scroll if table is too wide */
}

.table {
  width: 100%;
  table-layout: fixed; /* keeps column widths consistent */
  border-collapse: collapse; /* removes space between cells */
}

.table th,
.table td {
  word-wrap: break-word; /* allows breaking long words */
  white-space: normal; /* allows wrapping to next line */
  font-size: 0.9em;
  color: #000;
  padding: 0 !important; /* remove all padding */
  margin: 0 !important;
  border: 1px solid #ddd; /* optional: keeps visible structure */
  vertical-align: top; /* aligns content nicely */
}

/*  */

.show-more {
  margin-top: 0.4rem;
  text-align: right;
}

.show-more a {
  font-size: 0.8rem;
  color: #1e88e5;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.show-more a:hover {
  color: #0d47a1;
  text-decoration: underline;
}
</style>
